import * as _ from 'lodash';
import debug from 'debug';
import { sprintf } from 'sprintf-js';
import {
  GetTextTranslations,
  mergeTranslationsIn,
  getTranslationsLanguage,
} from './po';
import { convertText, TextConversionTypes } from './conversions';
import * as plurals from './plurals';
import type { GettextTranslateOptions } from './types';

declare global {
  interface Window {
    $translate: GettextTranslate;
  }
}

const log = debug('itp.tools.gettext.translate');

function toTextConversionTypes(
  conversion: string | string[]
): TextConversionTypes[] {
  if (_.isString(conversion)) {
    conversion = [conversion];
  }

  const result: TextConversionTypes[] = [];
  for (let conv of conversion) {
    const tc = getTextConversionValue(conv.replace('Case', ''));
    if (tc) {
      result.push(tc);
    }
  }
  return result;
}

function getTextConversionValue(key: string) {
  return Object.entries(TextConversionTypes).find(([k, val]) => k === key)?.[1];
}

function interpolatePlural(message: string, n: number) {
  if (_.isNumber(n)) {
    return message.replace(/\$\{n\}/gim, n.toString());
  }
  return message;
}

export class GettextTranslate {
  readonly defaultLanguage: string;
  private $$language: string;
  private $$silent: boolean;
  translations: Record<string, GetTextTranslations>;

  constructor(args: {
    translations: GetTextTranslations[];
    defaultLanguage: string;
    silent?: boolean;
  }) {
    this.defaultLanguage = args.defaultLanguage;
    this.translations = {};

    for (let trans of args.translations) {
      let lang = getTranslationsLanguage(trans);
      if (this.translations[lang]) {
        mergeTranslationsIn(this.translations[lang], trans);
        // throw new Error(`Translations for language ${lang} already exists.`);
        continue;
      }
      this.translations[lang] = trans;
    }
    this.$$language = args.defaultLanguage;
    this.$$silent = args.silent || false;

    if (!window.$translate) {
      // HACK
      window.$translate = this;
    }
  }

  get language() {
    return this.$$language;
  }

  set language(value: string) {
    log('set language to "%s"', value);
    this.$$language = value;
  }

  get silent() {
    return this.$$silent;
  }

  private convertInput(
    conversion: string | string[],
    msgid: string,
    plural?: string
  ) {
    const ct = toTextConversionTypes(conversion);
    msgid = convertText(msgid, ct);

    if (plural) {
      plural = convertText(plural, ct);
    }
    return { msgid, plural };
  }

  private interpolateVars(
    result: string,
    n?: number,
    plural?: string,
    args?: Record<string, any>
  ) {
    if (result) {
      if (plural && _.isNumber(n)) {
        result = interpolatePlural(result, n);
      }
    }
    return args ? sprintf(result, args) : result;
  }

  /**
   * Get the translated string from the translation dictionary.
   *
   * @param msgid The translation key
   * @param n The number to switch between singular and plural
   * @param context The translation key context
   * @param plural The default plural value
   * @param language The language ID
   */
  translate(opts: GettextTranslateOptions) {
    if (!opts.msgid) {
      return '';
    }


    let { msgid, n, context, language, plural, args, conversion } = opts;

    if (!language) {
      language = this.$$language;
    }

    if (n == null) {
      n = 1;
    }

    if (conversion) {
      const converted = this.convertInput(conversion, msgid, plural);
      msgid = converted.msgid;
      plural = converted.plural;
    }

    const silent = this.silent || language === this.$$language;

    const untranslated =
      plural && plurals.getTranslationIndex(language, n) > 0 ? plural : msgid;

    const translations = this.translations[language];
    if (!translations) {
      if (!silent) {
        console.warn(`No translations found for ${language}`);
      }
      return this.interpolateVars(untranslated, n, plural, args);
    }

    const ctx = translations.translations[context || ''];
    if (!ctx) {
      if (!silent) {
        console.warn(
          `No context ${context} found. msgid=${msgid}, language=${language}`
        );
      }
      return this.interpolateVars(untranslated, n, plural, args);
    }

    msgid = msgid.trim();

    const translated = ctx[msgid];
    const fuzzy = /\bfuzzy\b/.test(translated?.comments?.flag ?? '');
    if (!translated || _.isEmpty(translated) || fuzzy) {
      if (!silent) {
        let msg = `Untranslated ${language} key found: ${msgid}`;
        if (context) {
          msg += ` context=${context}`;
        }
        console.warn(msg, opts, this.defaultLanguage, this.$$language);
      }
      return this.interpolateVars(untranslated, n, plural, args);
    }

    const translationIndex =
      translated.msgstr.length === 1 && n === 1
        ? 0
        : plurals.getTranslationIndex(language, n);

    return this.interpolateVars(
      translated.msgstr[translationIndex],
      n,
      plural,
      args
    );
  }

  /**
   * Returns a string of the translation of the message.
   *
   * @param msgid The translation key
   * @returns The translated text
   */
  gettext(msgid: string, language?: string, args?: Record<string, any>) {
    return this.translate({
      msgid,
      language,
      args,
    });
  }

  /**
   * Returns a string of the translation for the given context.
   *
   * @param context The context of the string to translate
   * @param msgid The translation key
   * @returns The translated text
   */
  pgettext(
    context: string,
    msgid: string,
    language?: string,
    args?: Record<string, any>
  ) {
    return this.translate({
      msgid,
      context,
      language,
      args,
    });
  }

  /**
   * Returns a string of the translation of either the singular or plural,
   * based on the number.
   *
   * @param msgid The translation key
   * @param n   The number to switch between singular and plural
   * @param plural The plural form of the translation key.
   * @returns The translated text
   */
  ngettext(
    msgid: string,
    plural: string,
    n: number,
    language?: string,
    args?: Record<string, any>
  ) {
    return this.translate({ msgid, n, plural, language, args });
  }

  /**
   * Returns a string of the translation of either the singular or plural,
   * based on the number for the given context.
   *
   * @param context The context of the string to translate
   * @param msgid The translation key
   * @param n The number to switch between singular and plural
   * @param plural The plural form of the translation key.
   * @returns The translated text
   */
  npgettext(
    context: string,
    msgid: string,
    plural: string,
    n: number,
    language?: string,
    args?: Record<string, any>
  ) {
    return this.translate({
      msgid,
      n,
      context,
      plural,
      language,
      args,
    });
  }
}

/**
 * Returns a string of the translation of the message.
 * If the translator is not yet initialized, returns the msgid as fallback.
 *
 * @param msgid The translation key
 * @returns The translated text
 */
export function gettext(
  msgid: string,
  language?: string,
  args?: Record<string, any>
) {
  if (!window.$translate) {
    return msgid;
  }
  return window.$translate.gettext(msgid, language, args);
}

/**
 * Returns a string of the translation for the given context.
 * If the translator is not yet initialized, returns the msgid as fallback.
 *
 * @param context The context of the string to translate
 * @param msgid The translation key
 * @returns The translated text
 */
export function pgettext(
  context: string,
  msgid: string,
  language?: string,
  args?: Record<string, any>
) {
  if (!window.$translate) {
    return msgid;
  }
  return window.$translate.pgettext(context, msgid, language, args);
}

/**
 * Returns a string of the translation of either the singular or plural,
 * based on the number.
 * If the translator is not yet initialized, returns the msgid as fallback.
 *
 * @param msgid The translation key
 * @param n   The number to switch between singular and plural
 * @param plural The plural form of the translation key.
 * @returns The translated text
 */
export function ngettext(
  msgid: string,
  plural: string,
  n: number,
  language?: string,
  args?: Record<string, any>
) {
  if (!window.$translate) {
    return n === 1 ? msgid : plural;
  }
  return window.$translate.ngettext(msgid, plural, n, language, args);
}

/**
 * Returns a string of the translation of either the singular or plural,
 * based on the number for the given context.
 * If the translator is not yet initialized, returns the msgid as fallback.
 *
 * @param context The context of the string to translate
 * @param msgid The translation key
 * @param n The number to switch between singular and plural
 * @param plural The plural form of the translation key.
 * @returns The translated text
 */
export function npgettext(
  context: string,
  msgid: string,
  plural: string,
  n: number,
  language?: string,
  args?: Record<string, any>
) {
  if (!window.$translate) {
    return n === 1 ? msgid : plural;
  }
  return window.$translate.npgettext(context, msgid, plural, n, language, args);
}
